import React, { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  Calendar,
  MapPin,
  Users,
  Eye,
  Star,
  Tag,
  ChevronDown,
  ArrowRight,
  Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../utils/axios.config';
import { baseUrl } from '../../utils/url';

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('activityDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalActivities, setTotalActivities] = useState(0);
  const itemsPerPage = 12;

  // Fetch activities with filters and pagination
  const handleGetActivities = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        category: selectedCategory,
        status: 'published', // Only show published activities
        sortBy,
        sortOrder
      });

      const { data } = await api.get(`recent-activities/get?${params}`);
      
      if (data.success) {
        setActivities(data.data.activities);
        setTotalPages(Math.ceil(data.data.total / itemsPerPage));
        setTotalActivities(data.data.total);
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const handleGetCategories = async () => {
    try {
      const { data } = await api.get('activity-categories/get');
      if (data.success) {
        setCategories(data.data.categories.filter(cat => cat.isActive));
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  useEffect(() => {
    handleGetActivities();
  }, [currentPage, searchTerm, selectedCategory, sortBy, sortOrder]);

  useEffect(() => {
    handleGetCategories();
  }, []);

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('bn-BD', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get category color
  const getCategoryColor = (category) => {
    return category?.color || '#3B82F6';
  };

  if (loading && currentPage === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4 py-16">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            সকল কার্যক্রম
          </h1>
          <div className="w-24 h-1 bg-secondary mx-auto mb-4"></div>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6">
            আমাদের সংগঠনের সকল কার্যক্রম ও প্রকল্পের বিস্তারিত তথ্য এখানে পাবেন
          </p>
          <p className="text-sm text-gray-500">
            মোট {totalActivities} টি কার্যক্রম পাওয়া গেছে
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="কার্যক্রম খুঁজুন..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input input-bordered w-full pl-10"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="select select-bordered w-full"
              >
                <option value="">সকল বিভাগ</option>
                {categories.map(category => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                }}
                className="select select-bordered w-full"
              >
                <option value="activityDate-desc">নতুন প্রথম</option>
                <option value="activityDate-asc">পুরাতন প্রথম</option>
                <option value="title-asc">নাম অনুসারে (ক-য়)</option>
                <option value="views-desc">বেশি দেখা</option>
              </select>
            </div>
          </div>
        </div>

        {/* Activities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {activities.map((activity) => (
            <div key={activity._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={`${baseUrl}${activity.imageUrl}`}
                  alt={activity.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Featured Badge */}
                {activity.featured && (
                  <div className="absolute top-3 left-3">
                    <div className="bg-yellow-500 text-white px-2 py-1 rounded-full flex items-center text-xs font-medium">
                      <Star size={12} className="mr-1 fill-current" />
                      বিশেষ
                    </div>
                  </div>
                )}
                
                {/* Activity Date */}
                <div className="absolute top-3 right-3 bg-secondary text-white px-3 py-1 rounded-full flex items-center text-xs font-medium">
                  <Calendar size={12} className="mr-1" />
                  <span>{formatDate(activity.activityDate)}</span>
                </div>
                
                {/* Category Badge */}
                {activity.category && (
                  <div className="absolute bottom-3 left-3">
                    <span 
                      className="px-2 py-1 rounded-full text-white text-xs font-medium"
                      style={{ backgroundColor: getCategoryColor(activity.category) }}
                    >
                      {activity.category.name}
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">
                  {activity.title}
                </h3>
                
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {activity.shortDescription}
                </p>

                {/* Meta Info */}
                <div className="space-y-2 mb-4">
                  {activity.location && (
                    <div className="flex items-center text-gray-500 text-xs">
                      <MapPin size={14} className="mr-2" />
                      <span>{activity.location}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center text-gray-500">
                      <Eye size={14} className="mr-1" />
                      <span>{activity.views || 0} বার দেখা হয়েছে</span>
                    </div>
                    
                    {activity.participants > 0 && (
                      <div className="flex items-center text-gray-500">
                        <Users size={14} className="mr-1" />
                        <span>{activity.participants} জন</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tags */}
                {activity.tags && activity.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {activity.tags.slice(0, 2).map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700"
                      >
                        <Tag size={10} className="mr-1" />
                        {tag}
                      </span>
                    ))}
                    {activity.tags.length > 2 && (
                      <span className="text-xs text-gray-500">
                        +{activity.tags.length - 2} আরো
                      </span>
                    )}
                  </div>
                )}

                {/* Read More Button */}
                <Link 
                  to={`/activities/${activity._id}`}
                  className="w-full bg-primary hover:bg-primary/90 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors duration-200 text-sm font-medium"
                >
                  বিস্তারিত জানুন
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {activities.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar size={32} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">কোন কার্যক্রম পাওয়া যায়নি</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || selectedCategory 
                ? 'আপনার ফিল্টার অনুযায়ী কোন কার্যক্রম পাওয়া যায়নি। অন্য অনুসন্ধান চেষ্টা করুন।'
                : 'এখনো কোন কার্যক্রম প্রকাশিত হয়নি।'
              }
            </p>
            <Link to="/" className="btn btn-primary">
              হোম পেজে ফিরে যান
            </Link>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2">
            <button 
              className="btn btn-outline"
              disabled={currentPage === 1 || loading}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              পূর্ববর্তী
            </button>
            
            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={page}
                    className={`btn btn-sm ${currentPage === page ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setCurrentPage(page)}
                    disabled={loading}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            <button 
              className="btn btn-outline"
              disabled={currentPage === totalPages || loading}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              পরবর্তী
            </button>
          </div>
        )}

        {/* Loading indicator for pagination */}
        {loading && currentPage > 1 && (
          <div className="flex justify-center mt-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Activities;